// Small-multiple areas on ONE shared vertical scale — the per-document
// ridgeline (X6).
//
// THE SHARED SCALE IS THE ENTIRE POINT. Given a row each, a document with 30
// visits and one with 1,700 both fill their own band and read as equally
// busy; the chart then says the opposite of what the data says. `max` is
// therefore computed across every row and passed to all of them, and a caller
// that wants per-row scaling has to build a different component and defend it.
//
// `null` is a gap. A document published thirty days into a ninety-day window
// has no traffic to report for the first sixty, and a line drawn along the
// axis there claims nobody read it — the same lie `Sparkline` exists to avoid.

import { Figure } from "./Figure";
import { areaPath, linePath, segments } from "./geometry";
import { linear, maxOf } from "./scale";
import "./charts.css";

export type RidgeRow = {
  key: string;
  label: string;
  /** Oldest first. `null` where the day was not measurable. */
  values: Array<number | null>;
};

export type RidgeProps = {
  label: string;
  rows: RidgeRow[];
  width?: number;
  /** Height of one row's area. Rows overlap by a third of this, so a tall
   *  peak reaches into its neighbour the way a ridgeline should. */
  rowHeight?: number;
  notMeasured?: { reason: string };
};

export function Ridge({
  label,
  rows,
  width = 560,
  rowHeight = 34,
  notMeasured,
}: RidgeProps) {
  // One ceiling for every row. See the header.
  const top = maxOf(rows.flatMap((row) => row.values));
  const scale = linear(top, rowHeight);
  const step = Math.round(rowHeight * 0.68);
  const height = Math.max(rowHeight, (rows.length - 1) * step + rowHeight);
  const columns = Math.max(...rows.map((row) => row.values.length), 1);
  const dx = columns > 1 ? width / (columns - 1) : 0;

  return (
    <Figure label={label} width={width} height={height} notMeasured={notMeasured}>
      {/* Painted back to front so a nearer row occludes the one behind it —
          the fill is opaque for exactly that reason. */}
      {rows.map((row, index) => {
        const baseline = index * step + rowHeight;
        const points = row.values.map((value, column) =>
          value === null
            ? null
            : ([column * dx, baseline - scale(value)] as [number, number]),
        );
        const runs = segments(points);
        return (
          <g key={row.key} className="maisight-chart-ridge-row">
            {runs.map((run) => (
              <g key={`${row.key}-${run[0][0]}`}>
                <path className="maisight-chart-ridge-fill" d={areaPath(run, baseline)} />
                <path className="maisight-chart-ridge-line" d={linePath(run)} />
                {run.length === 1 ? (
                  <circle
                    className="maisight-chart-ridge-dot"
                    cx={run[0][0]}
                    cy={run[0][1]}
                    r={1.5}
                  />
                ) : null}
              </g>
            ))}
          </g>
        );
      })}
    </Figure>
  );
}
